import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  makeStyles,
  useTheme,
} from "@civicplus/preamble-ui/lib/Utilities/ThemeHelper";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import {
  formatDate,
  getFormattedDateStringYearMonthDay,
  parseDate,
} from "@civicplus/preamble-ui/lib/Utilities/DateHelper";
import { updateEventFilters, updateSelectedDate } from "../../actions";
import debounce from "lodash/debounce";
import { removeStringDateTimeZone } from "../../utils";
import { v1Api, ApiResponse } from "../../apis";
import { useSnackbar } from "notistack";
import { rowQuerySelector } from "./EventListRow";
import { StaticDatePicker } from "@mui/x-date-pickers/StaticDatePicker";
import { styled, ThemeProvider } from "@mui/material/styles";
import {
  LocalizationProvider,
  CalendarPickerSkeleton,
  PickersDay,
  PickersDayProps,
} from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import DateFilter from "./filters/DateFilter";
import { lighten } from "@mui/material";
import classNames from "classnames";
import Tooltip from "@civicplus/preamble-ui/lib/Tooltip";
import { AppDispatch, RootState } from "../../app/store";
import { isCancel } from "axios";
const useStyles = makeStyles(theme => ({
  root: {
    "@container mainContainer (width < 800px)": {
      width: "280px",
    },
    "@container mainContainer (width > 800px)": {
      minWidth: theme.spacing(36),
    },
  },
  dateFilterSpacing: { padding: "0 2px" },
  dateFilterBetween: {
    backgroundColor: lighten(theme.palette.primary.light, 0.88),
  },
  dateFilterStart: { borderRadius: "50% 0% 0% 50%" },
  dateFilterEnd: { borderRadius: "0% 50% 50% 0%" },
  sameDate: { borderRadius: "50%" },
  MuiPicker: {
    "& .MuiPickersDay-root.Mui-disabled": {
      fontWeight: theme.typography.fontWeightRegular,
    },
    "@container mainContainer (width < 800px)": {
      "& .Mui-selected": {
        color: "#fff !important;",
      },
      "& .MuiPickerStaticWrapper-content": {
        minWidth: theme.spacing(35),
      },
      "& .MuiDayPicker-weekContainer, & .MuiDayPicker-header, & .MuiDayPicker-loadingContainer, & .MuiMonthPicker-root, & .MuiYearPicker-root":
        {
          width: "275px",
        },
      "& .MuiPickersCalendarHeader-root": {
        paddingRight: theme.spacing(6),
      },
    },
  },
  disabledDate: {
    color: `${theme.palette.grey[600]} !important`,
    cursor: "not-allowed !important",
  },
  enabledDate: {
    "&.MuiPickersDay-root": {
      //font is not withing preamble theme provider. Cant create this new value.
      fontWeight: "800 !important",
      //Spacing for aligning bigger font.
      paddingBottom: theme.spacing(0.38),
    },
  },
}));

interface CustomPickerDayProps extends PickersDayProps<string> {
  dayIsBetween: boolean;
  isFirstDay: boolean;
  isLastDay: boolean;
  isLastWeekDay: boolean;
  isFistWeekDay: boolean;
  isDisabled: boolean;
}

const EventSidebar: React.FC = () => {
  const eventFilters = useSelector((state: RootState) => state.eventFilters);
  const selectedDate = useSelector((state: RootState) => state.selectedDate);
  const dispatch = useDispatch<AppDispatch>();
  const [datePickerReady, setDatePickerReady] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const [datesWithEvents, setDatesWithEvents] = useState<Set<string>>(
    new Set()
  );
  const requestController = useRef<AbortController>();
  const [view, setView] = useState("day");
  const classes = useStyles();
  const theme = useTheme();
  const isDesktopView = useMediaQuery(theme.breakpoints.up("lg"));

  const onChangeDate = (value: string | null) => {
    const date = value ? new Date(value) : new Date();
    date.setHours(0, 0, 0);
    dispatch(updateSelectedDate(date.toDateString()));

    dispatch(
      updateEventFilters({
        calendarDate: date.toDateString(),
      })
    );
  };

  const findFirstElementInViewPort = (elements: NodeListOf<Element>) =>
    Array.prototype.find.call(
      elements,
      element => element.getBoundingClientRect().y >= 260
    );

  const getDatesWithEvents = useCallback(
    async (date: string, categories: number[]) => {
      // If there was a previous request in progress still, cancel it
      requestController.current?.abort();

      const selectedDate = new Date(date);
      selectedDate.setHours(0, 0, 0);
      const dates: Set<string> = new Set();
      try {
        const monthDate = new Date(
          getFormattedDateStringYearMonthDay(selectedDate, "")
        );

        requestController.current = new AbortController();
        const response: ApiResponse<string[]> = await v1Api.get(
          `/Events/GetDaysInMonthWithEvents(date='${
            monthDate.toISOString().split("T")[0]
          }',categories=${categories ? JSON.stringify(categories) : "[]"})`,
          {
            signal: requestController.current?.signal,
          }
        );

        if (response && response.data.value?.length) {
          response.data.value.forEach(dateWithEvent => {
            // Was implemented to replace '-' characters for '/', since firefox just
            // accepts dates with '/' characters *That's not a permanent fix, it should be fixed
            // in getFormattedDateStringYearMonthDay within Preamble
            const firefoxCompatibleDateFormat = dateWithEvent.replace(
              /-/g,
              "/"
            );

            dates.add(
              getFormattedDateStringYearMonthDay(
                firefoxCompatibleDateFormat,
                ""
              )
            );
          });
        }
      } catch (e) {
        if (isCancel(e)) {
          // Do nothing as this was intentionally cancelled
        } else {
          enqueueSnackbar("There was an error getting the dates with events", {
            variant: "error",
          });
        }
      }
      return dates;
    },
    [enqueueSnackbar]
  );

  const handleMonthChange = useCallback(
    async (date: string) => {
      const newDates = await getDatesWithEvents(
        date,
        eventFilters.categories ?? []
      );
      setDatesWithEvents(newDates);
    },
    [eventFilters.categories, getDatesWithEvents]
  );

  const loadMonth = useCallback(
    async (date: string) => {
      dispatch(updateSelectedDate(date));
      await handleMonthChange(date);
    },
    [dispatch, handleMonthChange]
  );

  const scrollEventListener = useCallback(() => {
    const events = document.querySelectorAll(rowQuerySelector);
    if (events) {
      const element = findFirstElementInViewPort(events);
      if (element) {
        const dateElement = element.getAttribute("data-date");
        const dateValue = new Date(removeStringDateTimeZone(dateElement));
        loadMonth(dateValue.toDateString());
      }
    }
  }, [loadMonth]);

  useEffect(() => {
    const ctn = document.getElementById("scroll-wrap");
    const debouncedListener = debounce(scrollEventListener, 50);
    ctn?.addEventListener("scroll", debouncedListener);
    return () => {
      ctn?.removeEventListener("scroll", debouncedListener);
    };
  }, [scrollEventListener]);

  useEffect(() => {
    let isCancelled = false;
    const init = async () => {
      const newDates = await getDatesWithEvents(new Date().toString(), []);
      if (!isCancelled) {
        setDatePickerReady(true);
        setDatesWithEvents(newDates);

        // We need to remove the aria-live from this month header
        const header = document?.querySelector(
          ".MuiPickersCalendarHeader-labelContainer"
        );
        header?.setAttribute("aria-live", "off");
        header?.setAttribute("role", "combobox");
        header?.setAttribute("aria-label", "Select Month and Year");
        header?.removeAttribute("aria-live");
      }
      // We need to remove the aria-live from this month header
      const header = document?.querySelector(
        ".MuiPickersCalendarHeader-labelContainer"
      );
      header?.setAttribute("aria-live", "off");
      header?.setAttribute("role", "combobox");
      header?.setAttribute("aria-label", "Select Month and Year");
      header?.removeAttribute("aria-live");
    };
    init();
    // We need to cancel any state updates if the component unmounts to prevent memory leaks
    return () => {
      isCancelled = true;
    };
  }, [getDatesWithEvents]);

  const RangePickersDay = styled(PickersDay, {
    shouldForwardProp: prop =>
      prop !== "dayIsBetween" &&
      prop !== "isFirstDay" &&
      prop !== "isLastDay" &&
      prop !== "isLastWeekDay" &&
      prop !== "isFistWeekDay" &&
      prop !== "isDisabled",
  })<CustomPickerDayProps>(
    ({
      theme,
      dayIsBetween,
      isFirstDay,
      isLastDay,
      isLastWeekDay,
      isFistWeekDay,
      isDisabled,
    }) => ({
      ...(dayIsBetween && {
        borderRadius: "50%",
        backgroundColor: "transparent",
        color: theme.palette.getContrastText(
          lighten(theme.palette.primary.light, 0.88)
        ),
        "&:hover, &:focus": {
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
        },
      }),
      ...((isFistWeekDay || isLastWeekDay || isFirstDay || isLastDay) && {
        backgroundColor: "transparent",
      }),
      ...((isFirstDay || isLastDay) && {
        borderRadius: "50%",
        backgroundColor: lighten(theme.palette.primary.light, 0.3),
        color: `${theme.palette.getContrastText(
          lighten(theme.palette.primary.light, 0.3)
        )}`,
      }),
      ...(isDisabled &&
        dayIsBetween && {
          color: `${lighten(
            theme.palette.getContrastText(
              lighten(theme.palette.primary.light, 0.88)
            ),
            0.35
          )} !important`,
          "&:hover, &:focus": {
            backgroundColor: lighten(theme.palette.primary.light, 0.3),
          },
          cursor: "not-allowed !important",
        }),
      ...(isDisabled &&
        (isFirstDay || isLastDay) && {
          color: `${theme.palette.getContrastText(
            lighten(theme.palette.primary.light, 0.3)
          )} !important`,
        }),
      ...(!isDisabled &&
        dayIsBetween && {
          //font is not withing preamble theme provider. Cant create this new value.
          fontWeight: 800,
        }),
      ...(isDisabled &&
        !dayIsBetween && {
          color: `${theme.palette.grey[600]} !important`,
          cursor: "not-allowed !important",
        }),
    })
  ) as React.ComponentType<CustomPickerDayProps>;

  const renderDay = (
    day: string,
    selectedDays: string[],
    DayComponentProps: PickersDayProps<string>
  ) => {
    //Extracting onFocus due to conflicts with PickerDays inside of Tooltips
    const { onFocus, ...otherDayComponentProps } = DayComponentProps;
    const hasEvent: boolean = datesWithEvents?.has(
      getFormattedDateStringYearMonthDay(day, "")
    );

    const disabledDate = (): boolean => {
      const date = Date.parse(day);
      if (eventFilters.date?.from && Date.parse(eventFilters.date?.from) > date)
        return true;
      else if (
        eventFilters.date?.to &&
        Date.parse(eventFilters.date?.to) < date
      )
        return true;
      else if (hasEvent) return false;
      return true;
    };

    const shouldDisableDate = disabledDate();
    const friendlyDate = formatDate(new Date(day), "MMMM d, yyyy");
    const title = shouldDisableDate
      ? `There are no events on ${friendlyDate}`
      : "";
    const toolTipKey = DayComponentProps.key + "tooltip";

    if (eventFilters.date?.from && eventFilters.date?.to) {
      const startDate = Date.parse(eventFilters.date?.from);
      const endDate = Date.parse(eventFilters.date?.to);
      const sameDate = startDate === endDate;
      const checkDate = Date.parse(day);
      const isBetween = checkDate >= startDate && checkDate <= endDate;
      const isStart = checkDate === startDate;
      const isEnd = checkDate === endDate;
      const isLastWeekDay = new Date(day).getDay() === 6 && isBetween;
      const isFirstWeekDay = new Date(day).getDay() === 0 && isBetween;

      return (
        <div
          className={classNames(
            classNames(classes.dateFilterSpacing, {
              [classes.dateFilterBetween]:
                (isBetween || isStart || isEnd) &&
                !DayComponentProps.outsideCurrentMonth,
              [classes.dateFilterEnd]: isLastWeekDay || isEnd,
              [classes.dateFilterStart]: isFirstWeekDay || isStart,
              [classes.sameDate]: sameDate || (isLastWeekDay && isStart),
            })
          )}
        >
          <Tooltip title={title} placement="top" key={toolTipKey}>
            <RangePickersDay
              {...otherDayComponentProps}
              disableMargin={true}
              selected={
                hasEvent && selectedDate === new Date(day).toDateString()
              }
              aria-disabled={shouldDisableDate ? true : undefined}
              onDaySelect={
                shouldDisableDate ? () => false : DayComponentProps.onDaySelect
              }
              dayIsBetween={isBetween}
              isFirstDay={isStart}
              isLastDay={isEnd}
              isDisabled={shouldDisableDate}
              isLastWeekDay={isLastWeekDay}
              isFistWeekDay={isFirstWeekDay}
            />
          </Tooltip>
        </div>
      );
    }

    return (
      <Tooltip title={title} placement="top" key={toolTipKey}>
        <PickersDay
          {...otherDayComponentProps}
          selected={hasEvent && selectedDate === new Date(day).toDateString()}
          aria-disabled={shouldDisableDate ? true : undefined}
          onDaySelect={
            shouldDisableDate ? () => false : DayComponentProps.onDaySelect
          }
          className={classNames({
            [classes.disabledDate]: shouldDisableDate,
            [classes.enabledDate]: !shouldDisableDate,
          })}
        />
      </Tooltip>
    );
  };

  /**
   *  Workaround for keyboard navigation not firing onMonthChange for StaticDatePicker
   *  Remove this if MUI-X fixes this:
   *  https://github.com/mui/mui-x/issues/6953
   */
  const [hasRendered, setHasRendered] = useState(false);
  useEffect(() => {
    // Need to make sure the calendar has rendered before adding MutationObserver
    setHasRendered(true);

    // Need a reference to the observer to disconnect it
    let observer: MutationObserver | undefined;

    if (hasRendered) {
      const monthHeaderChanged = (e: MutationRecord[]) => {
        // Only detect the new month being added, not the old month being removed
        if (e.length && e[0].addedNodes?.length) {
          const newDateString = (e[0].addedNodes[0] as HTMLElement).innerText;
          const formattedDate = (
            parseDate(newDateString, "MMMM yyyy") as Date
          ).toString();
          handleMonthChange(formattedDate);
        }
      };

      observer = new MutationObserver(monthHeaderChanged);
      const element = document?.querySelector(
        ".MuiPickersCalendarHeader-labelContainer .MuiPickersFadeTransitionGroup-root"
      ) as HTMLElement;
      if (element) {
        observer.observe(element, {
          childList: true,
          // subtree: true,
          // characterData: true,
        });
      }
    }

    return () => {
      observer?.disconnect();
    };
  }, [handleMonthChange, hasRendered]);

  return (
    <div className={classes.root} data-testid="dateRangeContainer">
      {isDesktopView && (
        <>
          <aside aria-label="Navigate to events from a specific date">
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <ThemeProvider theme={theme}>
                <StaticDatePicker
                  className={classes.MuiPicker}
                  value={selectedDate ? new Date(selectedDate) : undefined}
                  renderLoading={() => (
                    <CalendarPickerSkeleton data-testid="picker-loader" />
                  )}
                  onChange={e => {
                    if (view === "day") onChangeDate(e);
                  }}
                  views={["year", "month", "day"]}
                  onViewChange={setView}
                  renderInput={() => <></>}
                  loading={!datePickerReady}
                  // onMonthChange={handleMonthChange} // Disabled for workaround - re-enable once keyboard events fire this
                  displayStaticWrapperAs="desktop"
                  renderDay={renderDay}
                />
              </ThemeProvider>
            </LocalizationProvider>
          </aside>
          <DateFilter />
        </>
      )}
    </div>
  );
};

export default EventSidebar;
